import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/StatusBadge";
import {
  LayoutDashboard, Users as UsersIcon, ListChecks, Coins,
  ScrollText, AlertTriangle, ShieldCheck, LogOut, Layers, UploadCloud, Inbox, MessageSquare, Store,
} from "lucide-react";
import { NoirBg } from "@/components/NoirBg";

const NAV = [
  { to: "/dashboard", label: "Panel", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/subir", label: "Subir archivo", icon: UploadCloud, testid: "nav-upload" },
  { to: "/users", label: "Usuarios", icon: UsersIcon, testid: "nav-users" },
  { to: "/jobs", label: "Trabajos", icon: ListChecks, testid: "nav-jobs" },
  { to: "/solicitudes", label: "Solicitudes", icon: Inbox, testid: "nav-requests" },
  { to: "/conversaciones", label: "Ver conversaciones", icon: MessageSquare, testid: "nav-conversations" },
  { to: "/admin/mercado", label: "Mercado", icon: Store, testid: "nav-market-admin" },
  { to: "/costs", label: "Costos", icon: Coins, testid: "nav-costs" },
  { to: "/audit", label: "Auditoría", icon: ScrollText, testid: "nav-audit" },
  { to: "/errors", label: "Errores", icon: AlertTriangle, testid: "nav-errors" },
  { to: "/admins", label: "Administradores", icon: ShieldCheck, testid: "nav-admins" },
];

/** Globito rojo con las solicitudes de créditos sin aprobar. */
const PendingDot = ({ count }) => (
  <span data-testid="nav-requests-badge"
    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold text-white
               flex items-center justify-center call-ring"
    style={{ background: "linear-gradient(135deg,#B4560E,#2A1403)" }}>
    {count > 99 ? "99+" : count}
  </span>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: pendingData } = useQuery({
    queryKey: ["credit-requests-pending"],
    queryFn: async () => (await api.get("/admin/credit-requests",
                                        { params: { status: "PENDIENTE", limit: 1 } })).data,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const pending = pendingData?.pending || 0;

  return (
    <div className="min-h-screen flex noir-bg theme-noir" style={{ backgroundColor: "#050507" }}>
      <NoirBg />
      <aside className="fixed left-0 top-0 h-screen w-64 border-r flex flex-col z-40 backdrop-blur-xl"
        style={{ backgroundColor: "rgba(10,14,22,0.72)", borderColor: "#1E293B" }} data-testid="sidebar">
        <div className="h-16 flex items-center gap-3 px-5 border-b" style={{ borderColor: "#1E293B" }}>
          <img src="/logo-digital-forge.jpg" alt="Digital Forge" data-testid="brand-logo"
              className="w-9 h-9 rounded-md object-cover shrink-0 border border-[#FF8A2B]/40"
              style={{ boxShadow: "0 0 14px rgba(255,138,43,0.28)" }} />
          <div className="leading-tight">
            <div className="font-heading font-bold text-sm text-slate-100">Digital Forge</div>
            <div className="text-[10px] text-slate-500 font-mono">TELEGRAM · OCR · AI</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "text-white" : "text-slate-400 hover:text-slate-100"
                }`}
              style={({ isActive }) => isActive
                ? { backgroundColor: "rgba(194, 98, 14,0.14)", boxShadow: "inset 2px 0 0 #C2620E" }
                : {}}>
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
              {n.to === "/solicitudes" && pending > 0 && <PendingDot count={pending} />}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t" style={{ borderColor: "#1E293B" }}>
          <div className="px-3 py-2 rounded-md mb-2" style={{ backgroundColor: "#111827" }}>
            <div className="text-[11px] text-slate-500">Sesión</div>
            <div className="font-mono text-xs text-slate-200">{user?.telegram_id}</div>
            <div className="mt-1"><RoleBadge role={user?.role} /></div>
          </div>
          <button data-testid="logout-btn" onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-400 hover:text-rose-400 transition-colors">
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-h-screen">{children}</main>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, right }) => (
  <div className="sticky top-0 z-30 h-16 backdrop-blur-md border-b px-8 flex items-center justify-between"
    style={{ backgroundColor: "rgba(10,14,22,0.55)", borderColor: "#1E293B" }}>
    <div>
      <h1 className="font-heading text-xl font-bold tracking-tight text-slate-100">{title}</h1>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </div>
    {right}
  </div>
);
